import { TaskRecurrence, RecurrenceFreq } from '../types/task';

/** Nap-pontos dátum (00:00). */
export const dayStart = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const isoWeekday = (d: Date) => ((d.getDay() + 6) % 7) + 1; // 1 = hétfő … 7 = vasárnap
const weekStart = (d: Date) => addDays(dayStart(d), -(isoWeekday(d) - 1));

/**
 * A `after` napja UTÁNI következő előfordulás.
 * (A Cloud Function ugyanezt a logikát futtatja – functions/recurringTasks.js.)
 */
export function nextOccurrence(rec: TaskRecurrence, after: Date): Date {
  const interval = Math.max(1, Math.floor(rec.interval || 1));
  const base = dayStart(after);
  switch (rec.freq) {
    case 'daily':
      return addDays(base, interval);
    case 'weekly': {
      const days = (rec.weekdays && rec.weekdays.length ? rec.weekdays : [isoWeekday(base)]).slice().sort((a, b) => a - b);
      const anchorWeek = weekStart(rec.anchor ? dayStart(rec.anchor) : base);
      for (let i = 1; i <= 7 * interval * 2 + 7; i++) {
        const c = addDays(base, i);
        if (!days.includes(isoWeekday(c))) continue;
        const weeks = Math.round((weekStart(c).getTime() - anchorWeek.getTime()) / (7 * 86400000));
        if (((weeks % interval) + interval) % interval === 0) return c;
      }
      return addDays(base, 7 * interval);
    }
    case 'monthly': {
      const dom = rec.dayOfMonth || base.getDate();
      const m0 = base.getMonth() + interval;
      const y = base.getFullYear() + Math.floor(m0 / 12), m = ((m0 % 12) + 12) % 12;
      return new Date(y, m, Math.min(dom, daysInMonth(y, m)), 0, 0, 0, 0);
    }
    case 'yearly': {
      const y = base.getFullYear() + interval;
      const m = (rec.month || base.getMonth() + 1) - 1;
      return new Date(y, m, Math.min(rec.dayOfMonth || base.getDate(), daysInMonth(y, m)), 0, 0, 0, 0);
    }
    default:
      return addDays(base, interval);
  }
}

/** Első előfordulás egy kezdő dátumtól (a kezdőnap maga is lehet). */
export function firstOccurrence(rec: TaskRecurrence, from: Date): Date {
  const f = dayStart(from);
  if (rec.freq === 'weekly') {
    const days = rec.weekdays && rec.weekdays.length ? rec.weekdays : [isoWeekday(f)];
    if (days.includes(isoWeekday(f))) return f;
    return nextOccurrence({ ...rec, interval: 1, anchor: f }, f);
  }
  if (rec.freq === 'monthly' && rec.dayOfMonth) {
    const c = new Date(f.getFullYear(), f.getMonth(), Math.min(rec.dayOfMonth, daysInMonth(f.getFullYear(), f.getMonth())));
    return c >= f ? c : nextOccurrence({ ...rec, interval: 1 }, f);
  }
  if (rec.freq === 'yearly' && rec.month && rec.dayOfMonth) {
    const c = new Date(f.getFullYear(), rec.month - 1, Math.min(rec.dayOfMonth, daysInMonth(f.getFullYear(), rec.month - 1)));
    return c >= f ? c : nextOccurrence({ ...rec, interval: 1 }, f);
  }
  return f;
}

/** Következő N előfordulás előnézethez. */
export function previewOccurrences(rec: TaskRecurrence, count = 5): Date[] {
  const out: Date[] = [];
  let cur = rec.nextRunAt ? dayStart(rec.nextRunAt) : firstOccurrence(rec, new Date());
  for (let i = 0; i < count; i++) {
    if (rec.endDate && cur > dayStart(rec.endDate)) break;
    out.push(cur);
    cur = nextOccurrence(rec, cur);
  }
  return out;
}

const WD = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
const MONTHS = ['január', 'február', 'március', 'április', 'május', 'június', 'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];

/** Emberi leírás: „Hetente hétfőn és csütörtökön”, „2 havonta 15-én”, „Évente március 1-jén”. */
export function describeRecurrence(rec: TaskRecurrence): string {
  const n = Math.max(1, rec.interval || 1);
  switch (rec.freq) {
    case 'daily': return n === 1 ? 'Naponta' : `${n} naponta`;
    case 'weekly': {
      const days = (rec.weekdays || []).slice().sort((a, b) => a - b).map(d => WD[d - 1]).join(', ');
      return `${n === 1 ? 'Hetente' : `${n} hetente`}${days ? ` (${days})` : ''}`;
    }
    case 'monthly': return `${n === 1 ? 'Havonta' : `${n} havonta`}${rec.dayOfMonth ? ` ${rec.dayOfMonth}-én` : ''}`;
    case 'yearly': return `${n === 1 ? 'Évente' : `${n} évente`}${rec.month ? ` ${MONTHS[rec.month - 1]} ${rec.dayOfMonth || 1}-én` : ''}`;
    default: return '';
  }
}

export const FREQ_OPTIONS: { value: RecurrenceFreq; label: string; unit: string }[] = [
  { value: 'daily', label: 'Naponta', unit: 'nap' },
  { value: 'weekly', label: 'Hetente', unit: 'hét' },
  { value: 'monthly', label: 'Havonta', unit: 'hónap' },
  { value: 'yearly', label: 'Évente', unit: 'év' }
];

export const defaultRecurrence = (): TaskRecurrence => {
  const start = dayStart(new Date());
  return { freq: 'monthly', interval: 1, dayOfMonth: start.getDate(), nextRunAt: start, leadDays: 7, active: true, createWorksheet: false, anchor: start };
};
